import React from 'react';
import { useTranslation } from 'react-i18next';
import ModalGeneric from '../Modal/ModalGeneric';
import ConversationProgressLegend from '../ConversationProgressLegend';
import ConversationProgressSummary from '../ConversationProgressSummary';
import { clampProgressIndex } from '../../utilities/activitySelection';
import { containsTerm, mergeConversationProgress, normalizeMatchText } from '../../utilities/conversationProgress';
import './ActivityDetailsModal.css';

const getVocabularyWords = (vocabulary) => {
  if (!Array.isArray(vocabulary)) return [];
  return vocabulary
    .map(item => (typeof item === 'string' ? item : item?.word || item?.term || null))
    .filter(Boolean);
};

const getVocabularyStatus = (word, progress) => {
  if (!progress) return null;

  const isInError = progress.errors.some(error => (
    containsTerm(error.text, word) || containsTerm(error.correction, word)
  ));
  if (isInError) return 'error';

  const isAcquired = progress.acquiredConcepts.some(concept => (
    containsTerm(concept.concept, word) || containsTerm(concept.evidence, word)
  ));
  return isAcquired ? 'acquired' : null;
};

const conceptsMatch = (current, historical) => {
  const currentConcept = normalizeMatchText(current?.concept);
  const historicalConcept = normalizeMatchText(historical?.concept);
  if (!currentConcept || !historicalConcept) return false;
  if (currentConcept === historicalConcept) return true;

  return containsTerm(historical?.evidence, current?.concept)
    || containsTerm(current?.evidence, historical?.concept);
};

const markPreviouslyAcquiredConceptsAsReviewed = (currentProgress, storedProgress) => {
  if (!currentProgress) return null;
  const historicalConcepts = storedProgress?.acquiredConcepts || [];

  return {
    ...currentProgress,
    acquiredConcepts: currentProgress.acquiredConcepts.map(concept => ({
      ...concept,
      status: historicalConcepts.some(historical => conceptsMatch(concept, historical))
        ? 'reviewed'
        : concept.status,
    })),
  };
};

/**
 * Details of the current learning-path activity. Intentionally sparse for
 * now — more sections will be added over time.
 *
 * The parent reads the activity document once when the session starts and
 * passes that immutable historical snapshot here. Opening Details performs no
 * Firebase reads; current-session highlights come only from local state.
 */
function ActivityDetailsModal({
  isOpen,
  onClose,
  activity,
  attemptProgressIndex,
  conversationProgress = null,
  activityDoc = null,
}) {
  const { t } = useTranslation();

  if (!isOpen || !activity) {
    return null;
  }

  const mirroredProgress = activity.progress || {};
  const bestProgressIndex = activityDoc?.bestProgressIndex
    ?? mirroredProgress.progressIndex;
  const attemptCount = activityDoc?.attemptCount ?? null;
  const vocabularyWords = getVocabularyWords(activity.vocabulary);
  const currentProgress = mergeConversationProgress(conversationProgress);
  const storedProgress = mergeConversationProgress(activityDoc && {
    progressIndex: activityDoc.bestProgressIndex ?? 0,
    errors: activityDoc.errors,
    acquiredConcepts: activityDoc.acquiredConcepts,
  });
  const progressSummary = markPreviouslyAcquiredConceptsAsReviewed(currentProgress, storedProgress);
  const allTimeProgress = mergeConversationProgress(storedProgress, progressSummary);
  const focus = activity.concept?.concept || null;
  const format = activity.communication_type?.activity || null;

  const facts = [
    { key: 'activity_details_current', value: `${clampProgressIndex(attemptProgressIndex)}%` },
    bestProgressIndex !== undefined && bestProgressIndex !== null
      ? { key: 'activity_details_best', value: `${clampProgressIndex(bestProgressIndex)}%` }
      : null,
    attemptCount ? { key: 'activity_details_attempts', value: String(attemptCount) } : null,
    focus && { key: 'activity_details_focus', value: focus },
    format && { key: 'activity_details_format', value: format },
  ].filter(Boolean);

  return (
    <ModalGeneric title={activity.title || ''} isOpen={isOpen} onClose={onClose}>
      <div className="activity-details">
        {activity.description && (
          <p className="activity-details__description">{activity.description}</p>
        )}

        <dl className="activity-details__facts">
          {facts.map(({ key, value }) => (
            <div className="activity-details__fact" key={key}>
              <dt>{t(key)}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>

        <ConversationProgressLegend />

        {progressSummary && (
          <ConversationProgressSummary
            conversationProgress={progressSummary}
            title={t('activity_details_summary')}
          />
        )}

        {vocabularyWords.length > 0 && (
          <div className="activity-details__vocabulary">
            <h4>{t('activity_details_vocabulary')}</h4>
            <ul>
              {vocabularyWords.map(word => {
                const status = getVocabularyStatus(word, allTimeProgress);
                const statusLabel = status === 'acquired'
                  ? t('activity_details_acquired')
                  : status === 'error'
                    ? t('activity_details_error')
                    : null;
                return (
                  <li
                    key={word}
                    className={status ? `activity-details__vocabulary-word--${status}` : undefined}
                    title={statusLabel || undefined}
                    aria-label={statusLabel ? `${word}: ${statusLabel}` : undefined}
                  >
                    {word}
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </ModalGeneric>
  );
}

export default ActivityDetailsModal;
